import { resourceKeys } from '@/api/hooks/keys'
import { useServerLog } from '@/api/hooks/useServerLog'
import * as Sentry from '@sentry/react'
import {
  focusManager,
  onlineManager,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import {
  defaultStallThresholds,
  evaluateStall,
  formatHealthLog,
  initialResumeTracking,
  isConstrainedConnection,
  isTransientResumeStall,
  severityFor,
  shouldEmitReport,
  trackResumeLifecycle,
  UpdateHealthSnapshot,
} from './updateHealth'

/** How often to sample update-health while mounted (local work only, no network). */
const SAMPLE_INTERVAL_MS = 15_000
/** Minimum gap between server-log reports on a normal connection. */
const REPORT_THROTTLE_MS = 60_000
/**
 * Minimum gap between reports on a bandwidth-constrained connection (data-saver
 * or 2g-class). Diagnostics must not compete with the app for scarce bandwidth.
 */
const REPORT_THROTTLE_CONSTRAINED_MS = 5 * 60_000
/**
 * Grace window after a resume during which a stale-data-only stall is treated as
 * the expected "refetch in flight" state and NOT reported. Must comfortably
 * exceed how long the post-resume refetch storm needs to land. A stall that
 * survives this window reports normally (= recovery genuinely failed).
 */
const RESUME_GRACE_MS = 20_000
/**
 * Window after a resume within which a reported stall is attributed to that
 * resume (the report carries how long the page was away).
 */
const RESUME_ATTRIBUTION_MS = 2 * 60_000
/**
 * Lifecycle events that should resume polling, logged so we can see whether they
 * actually arrive on a given device. Bound to the correct target: `freeze`,
 * `resume` (Chromium Page Lifecycle) and `visibilitychange` fire on `document`;
 * the rest on `window`. `freeze`/`resume` are the key signal for low-RAM Android
 * (e.g. cheap Samsung) where the OS suspends the whole web process.
 */
const DOCUMENT_LIFECYCLE_EVENTS = [
  'visibilitychange',
  'freeze',
  'resume',
] as const
const WINDOW_LIFECYCLE_EVENTS = [
  'focus',
  'blur',
  'online',
  'offline',
  'pageshow',
] as const

/** Minimal shape of the (non-standard) Network Information API we read. */
type NetworkInformationLike = {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
}

const readConnection = (): NetworkInformationLike | undefined => {
  if (typeof navigator === 'undefined') {
    return undefined
  }
  return (navigator as Navigator & { connection?: NetworkInformationLike })
    .connection
}

/** Approximate device RAM (GiB) — Chromium-only; low values indicate a cheap device. */
const readDeviceMemoryGb = (): number | null => {
  if (typeof navigator === 'undefined') {
    return null
  }
  return (
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null
  )
}

const readHardwareConcurrency = (): number | null => {
  if (typeof navigator === 'undefined') {
    return null
  }
  return navigator.hardwareConcurrency ?? null
}

/** `document.wasDiscarded` — tab was discarded under memory pressure and reloaded. */
const readWasDiscarded = (): boolean | null => {
  if (typeof document === 'undefined') {
    return null
  }
  const value = (document as Document & { wasDiscarded?: boolean }).wasDiscarded
  return value ?? null
}

/**
 * Observation-only monitor for the live-update polling loop.
 *
 * It samples react-query + browser visibility/focus/online state on an interval
 * and, when the page is visible but auto-updates appear stalled (stale visit
 * data or paused queries), reports a structured line to the backend log
 * (`useServerLog`) and a Sentry message. It also records every relevant
 * lifecycle event as a Sentry breadcrumb and logs the data-age gap whenever the
 * page becomes visible again — so a user report can reveal whether the
 * `visibilitychange`/focus signal that should resume polling was ever delivered.
 *
 * It takes NO corrective action (no refetch, no focusManager.setFocused), so it
 * cannot hide the bug it is meant to detect.
 */
export const useUpdateHealthMonitor = () => {
  const queryClient = useQueryClient()
  const serverLog = useServerLog()

  // Keep the latest serverLog in a ref so the long-lived listeners/intervals do
  // not need to be torn down and recreated as the hook re-renders.
  const serverLogRef = useRef(serverLog)
  serverLogRef.current = serverLog
  // Separate throttle clocks per severity so a recent warning can never delay
  // the first alertable error report (confirmed-stuck cases must get through).
  const lastWarnReportRef = useRef(0)
  const lastErrorReportRef = useRef(0)
  // Hidden/visible bookkeeping: when the page went away, how long it was away
  // on the last resume, and when it last became visible. Updated as a unit so a
  // resume's paired visibilitychange+focus events can't corrupt the away-time.
  const resumeTrackingRef = useRef(initialResumeTracking)

  const throttleMsFor = (snapshot: UpdateHealthSnapshot) =>
    isConstrainedConnection(snapshot)
      ? REPORT_THROTTLE_CONSTRAINED_MS
      : REPORT_THROTTLE_MS

  const collectSnapshot = (): UpdateHealthSnapshot => {
    const now = Date.now()
    const connection = readConnection()
    const cache = queryClient.getQueryCache()
    const all = cache.getAll()

    const visitQueries = cache.findAll({
      queryKey: resourceKeys('ServiceVisit').bySourceScope(),
      type: 'active',
    })
    const newestVisitUpdate = visitQueries.reduce(
      (max, q) => Math.max(max, q.state.dataUpdatedAt ?? 0),
      0
    )

    return {
      ts: now,
      visibilityState:
        typeof document === 'undefined' ? 'unknown' : document.visibilityState,
      documentHasFocus:
        typeof document === 'undefined' ? false : document.hasFocus(),
      navigatorOnLine:
        typeof navigator === 'undefined' ? true : navigator.onLine,
      reactQueryFocused: focusManager.isFocused(),
      reactQueryOnline: onlineManager.isOnline(),
      activeVisitQueries: visitQueries.length,
      visitDataAgeMs: newestVisitUpdate === 0 ? null : now - newestVisitUpdate,
      pausedQueryCount: all.filter(q => q.state.fetchStatus === 'paused')
        .length,
      erroredQueryCount: all.filter(q => q.state.status === 'error').length,
      fetchingQueryCount: all.filter(q => q.state.fetchStatus === 'fetching')
        .length,
      connectionInfoAvailable: connection !== undefined,
      connectionEffectiveType: connection?.effectiveType ?? null,
      connectionDownlinkMbps: connection?.downlink ?? null,
      connectionRttMs: connection?.rtt ?? null,
      connectionSaveData: connection?.saveData ?? false,
      wasDiscarded: readWasDiscarded(),
      deviceMemoryGb: readDeviceMemoryGb(),
      hardwareConcurrency: readHardwareConcurrency(),
    }
  }

  const report = (snapshot: UpdateHealthSnapshot) => {
    const evaluation = evaluateStall(snapshot, defaultStallThresholds)
    if (!evaluation.stalled) {
      return
    }
    const now = snapshot.ts
    const msSinceResume = now - resumeTrackingRef.current.resumeAt
    // Don't cry wolf right after a resume: stale data is expected until the
    // refetch lands. Only report once the stall outlives the grace window (or
    // if a genuinely-bad signal like a paused query is present).
    if (isTransientResumeStall(evaluation, msSinceResume, RESUME_GRACE_MS)) {
      return
    }

    // A stall reported within the attribution window after a resume means
    // auto-update did not recover once the device woke — the genuinely-stuck
    // case. Attribute it with how long the page was away.
    const recoveryFailed = msSinceResume < RESUME_ATTRIBUTION_MS
    const severity = severityFor(evaluation, recoveryFailed)

    // Throttle AFTER severity is known, on a per-severity clock, so a prior
    // warning within the window cannot suppress the first error.
    if (
      !shouldEmitReport(
        severity,
        now,
        lastWarnReportRef.current,
        lastErrorReportRef.current,
        throttleMsFor(snapshot)
      )
    ) {
      return
    }
    if (severity === 'error') {
      lastErrorReportRef.current = now
    } else {
      lastWarnReportRef.current = now
    }

    const focusStarved =
      snapshot.visibilityState === 'visible' && !snapshot.reactQueryFocused
    const extra = recoveryFailed
      ? {
          afterResumeAwayMs: resumeTrackingRef.current.awayMs,
          recoveryFailed: true,
        }
      : undefined

    const message = formatHealthLog(snapshot, evaluation, extra)
    try {
      // Escalate confirmed-stuck reports to error so they can be alerted on.
      if (severity === 'error') {
        serverLogRef.current.error(message)
      } else {
        serverLogRef.current.warn(message)
      }
    } catch {
      // Never let diagnostics throw into the app.
    }
    // Tags are filterable/alertable in Sentry; the watch-for signatures
    // (focus-starved, recovery-failed, paused) are first-class.
    Sentry.captureMessage(message, {
      level: severity,
      tags: {
        source: 'UpdateHealthMonitor',
        reasons: evaluation.reasons.join('|') || 'none',
        recoveryFailed: String(recoveryFailed),
        focusStarved: String(focusStarved),
      },
      extra: { ...snapshot, reasons: evaluation.reasons, ...extra },
    })
  }

  // Interval sampling.
  useEffect(() => {
    const id = setInterval(() => report(collectSnapshot()), SAMPLE_INTERVAL_MS)
    return () => clearInterval(id)
    // collectSnapshot/report close over stable refs + queryClient only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient])

  // Lifecycle-event breadcrumbs + on-resume gap logging.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const handler = (event: Event) => {
      const snapshot = collectSnapshot()
      Sentry.addBreadcrumb({
        category: 'update-health.lifecycle',
        level: 'info',
        message: event.type,
        data: {
          vis: snapshot.visibilityState,
          rqFocused: snapshot.reactQueryFocused,
          rqOnline: snapshot.reactQueryOnline,
          visitDataAgeMs: snapshot.visitDataAgeMs,
          paused: snapshot.pausedQueryCount,
        },
      })

      // Fold the event into resume tracking. Away-time is measured from when
      // the page went hidden (computed once per hidden→visible transition), so
      // the paired visibilitychange+focus/resume events on a single resume
      // can't clobber it. We record the resume here but do NOT report now: at
      // the instant of resume the data is necessarily stale (the refetch hasn't
      // landed), so an immediate report is a guaranteed false positive. If it
      // is STILL stale past the grace window the interval report fires —
      // carrying afterResumeAwayMs.
      resumeTrackingRef.current = trackResumeLifecycle(
        resumeTrackingRef.current,
        event.type,
        snapshot.visibilityState,
        snapshot.ts
      )
    }

    DOCUMENT_LIFECYCLE_EVENTS.forEach(type =>
      document.addEventListener(type, handler, { passive: true })
    )
    WINDOW_LIFECYCLE_EVENTS.forEach(type =>
      window.addEventListener(type, handler, { passive: true })
    )
    return () => {
      DOCUMENT_LIFECYCLE_EVENTS.forEach(type =>
        document.removeEventListener(type, handler)
      )
      WINDOW_LIFECYCLE_EVENTS.forEach(type =>
        window.removeEventListener(type, handler)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient])
}
